import dayjs from 'dayjs';

import * as Bixby from '$/core';

const formatDate = (value: Date | string | null) => {
  if (value === null || value === undefined) {
    return null;
  }

  const intermediateValue: Date | string =
    typeof value === 'string' && /[a-zA-Z]{3}-[0-9]{2}/.test(value as string) // Jun-19
      ? (value as string).replace(/-/g, '-01-20')
      : value;

  const date: dayjs.Dayjs = dayjs.utc(intermediateValue);
  if (!date.isValid()) {
    return null;
  }

  return Bixby.Utils.utcDateToString(date.toDate());
};

const formatCurrency = (value: number | null | undefined, includeCents: boolean) => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsedValue: number = parseFloat(value as any);
  if (isNaN(parsedValue)) {
    return null;
  }

  // add in appropriate formatting
  let prefix = '$';
  let suffix = '';
  if (parsedValue < 0) {
    prefix = prefix + '(';
    suffix = suffix + ')';
  }

  // remove hyphen
  const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

  let currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,');

  if (!includeCents) {
    currency = currency.split('.')[0];
  }

  return prefix + currency + suffix;
};

const formatPercentage = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsedValue: number = parseFloat(value as any);
  if (isNaN(parsedValue)) {
    return null;
  }

  const percentage: string = (parsedValue * 100).toFixed(1);
  return percentage + '%';
};

const formatMultiplier = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsedValue: number = parseFloat(value as any);
  if (isNaN(parsedValue)) {
    return null;
  }

  const multiplier: string = parsedValue.toFixed(1);
  return multiplier + 'x';
};

export abstract class GridFactory {
  public static buildAdminTransactionDetails(
    transactionDetails: Bixby.Models.DraftCompanyTransactionDetails
  ): Bixby.Models.AdminTransactionDetailGridRow[] {
    return [
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.TransactionDate,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TransactionDate,
        name: transactionDetails.transactionDateName,
        value: formatDate(transactionDetails.transactionDate),
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.TotalEnterpriseValue,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEnterpriseValue,
        name: transactionDetails.totalEnterpriseValueName,
        value: formatCurrency(transactionDetails.totalEnterpriseValue, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.RevenueAtBuyout,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.RevenueAtBuyout,
        name: transactionDetails.revenueAtBuyoutName,
        value: formatCurrency(transactionDetails.revenueAtBuyout, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.ManagementAdjustedEbitdaAtBuyout,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.ManagementAdjustedEbitdaAtBuyout,
        name: transactionDetails.managementAdjustedEbitdaAtBuyoutName,
        value: formatCurrency(transactionDetails.managementAdjustedEbitdaAtBuyout, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.TotalEnterpriseValueByRevenue,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEnterpriseValueByRevenue,
        name: transactionDetails.totalEnterpriseValueByRevenueName,
        value: formatMultiplier(transactionDetails.totalEnterpriseValueByRevenue) || 'NM',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.TotalEnterpriseValueByAdjustedManagementEbitda,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEnterpriseValueByAdjustedManagementEbitda,
        name: transactionDetails.totalEnterpriseValueByAdjustedManagementEbitdaName,
        value: formatMultiplier(transactionDetails.totalEnterpriseValueByAdjustedManagementEbitda) || 'NM',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.NetDebt,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.NetDebt,
        name: transactionDetails.netDebtName,
        value: formatCurrency(transactionDetails.netDebt, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.NetDebtPercentageOfCap,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.NetDebtPercentageOfCap,
        name: transactionDetails.netDebtPercentageOfCapName,
        value: formatPercentage(transactionDetails.netDebtPercentageOfCap) || 'NM',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.SponsorEquity,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.SponsorEquity,
        name: transactionDetails.sponsorEquityName,
        value: formatCurrency(transactionDetails.sponsorEquity, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.RolloverEquity,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.RolloverEquity,
        name: transactionDetails.rolloverEquityName,
        value: formatCurrency(transactionDetails.rolloverEquity, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.TotalEquity,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEquity,
        name: transactionDetails.totalEquityName,
        value: formatCurrency(transactionDetails.totalEquity, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.TotalEquityPercentageOfCap,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.TotalEquityPercentageOfCap,
        name: transactionDetails.totalEquityPercentageOfCapName,
        value: formatPercentage(transactionDetails.totalEquityPercentageOfCap) || 'NM',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.CumulativeDividends,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.CumulativeDividends,
        name: transactionDetails.cumulativeDividendsName,
        value: formatCurrency(transactionDetails.cumulativeDividends, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.ImpliedEquityAtRisk,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.ImpliedEquityAtRisk,
        name: transactionDetails.impliedEquityAtRiskName,
        value: formatCurrency(transactionDetails.impliedEquityAtRisk, false) || 'NM',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.SecuredDebt,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.SecuredDebt,
        name: transactionDetails.securedDebtName,
        value: formatCurrency(transactionDetails.securedDebt, false) || 'NA',
      },
      {
        key: Bixby.Enums.CompanyTransactionDetailsKeys.Cash,
        rowTitle: Bixby.Enums.CompanyTransactionDetailsRowTitles.Cash,
        name: transactionDetails.cashName,
        value: formatCurrency(transactionDetails.cash, false) || 'NA',
      },
    ];
  }

  public static buildClientCapitalStructure(
    ltm: Bixby.Models.DraftCompanyAnalyticsLastTwelveMonths,
    priorQuarter: Bixby.Models.DraftCompanyAnalyticsPriorQuarter,
    currentQuarter: Bixby.Models.DraftCompanyAnalyticsCurrentQuarter,
    creditors: Bixby.Models.DraftCompanyCreditor[]
  ): Bixby.Models.AdminIncomeStatementStatGridRow[] {
    const findCreditor = (debtType: Bixby.Enums.DebtType) => {
      const creditor = creditors.find(c => c.debtType === debtType);
      return creditor ? creditor.name : null;
    };

    return [
      {
        key: 'date',
        rowTitle: Bixby.Enums.CompanyAnalytics.date,
        lineItem: null,
        ltm: formatDate(ltm.date),
        priorQuarter: formatDate(priorQuarter.date),
        currentQuarter: formatDate(currentQuarter.date),
      },
      {
        key: 'revenue',
        rowTitle: Bixby.Enums.CompanyAnalytics.revenue,
        lineItem: null,
        ltm: formatCurrency(ltm.revenue, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.revenue, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.revenue, false) || 'NM',
      },
      {
        key: 'grossProfit',
        rowTitle: Bixby.Enums.CompanyAnalytics.grossProfit,
        lineItem: null,
        ltm: formatCurrency(ltm.grossProfit, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.grossProfit, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.grossProfit, false) || 'NM',
      },
      {
        key: 'ebit',
        rowTitle: Bixby.Enums.CompanyAnalytics.ebit,
        lineItem: null,
        ltm: formatCurrency(ltm.ebit, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ebit, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ebit, false) || 'NM',
      },
      {
        key: 'ebitda',
        rowTitle: Bixby.Enums.CompanyAnalytics.ebitda,
        lineItem: null,
        ltm: formatCurrency(ltm.ebitda, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ebitda, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ebitda, false) || 'NM',
      },
      {
        key: 'ebitdaAdjustments',
        rowTitle: Bixby.Enums.CompanyAnalytics.ebitdaAdjustments,
        lineItem: null,
        ltm: formatCurrency(ltm.ebitdaAdjustments, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ebitdaAdjustments, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ebitdaAdjustments, false) || 'NM',
      },
      {
        key: 'adjustedEbitda',
        rowTitle: Bixby.Enums.CompanyAnalytics.adjustedEbitda,
        lineItem: null,
        ltm: formatCurrency(ltm.adjustedEbitda, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.adjustedEbitda, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.adjustedEbitda, false) || 'NM',
      },
      {
        key: 'covenantEbitda',
        rowTitle: Bixby.Enums.CompanyAnalytics.covenantEbitda,
        lineItem: null,
        ltm: formatCurrency(ltm.covenantEbitda, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.covenantEbitda, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.covenantEbitda, false) || 'NM',
      },
      {
        key: 'ocf',
        rowTitle: Bixby.Enums.CompanyAnalytics.ocf,
        lineItem: null,
        ltm: formatCurrency(ltm.ocf, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ocf, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ocf, false) || 'NM',
      },
      {
        key: 'capitalExpenditures',
        rowTitle: Bixby.Enums.CompanyAnalytics.capitalExpenditures,
        lineItem: null,
        ltm: formatCurrency(ltm.capitalExpenditures, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.capitalExpenditures, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.capitalExpenditures, false) || 'NM',
      },
      {
        key: 'cash',
        rowTitle: Bixby.Enums.CompanyAnalytics.cash,
        lineItem: null,
        ltm: formatCurrency(ltm.cash, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'abl',
        rowTitle: Bixby.Enums.CompanyAnalytics.abl,
        lineItem: findCreditor(Bixby.Enums.DebtType.Abl),
        ltm: formatCurrency(ltm.abl, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'priorityDebt',
        rowTitle: Bixby.Enums.CompanyAnalytics.priorityDebt,
        lineItem: findCreditor(Bixby.Enums.DebtType.PriorityDebt),
        ltm: formatCurrency(ltm.priorityDebt, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'revolver',
        rowTitle: Bixby.Enums.CompanyAnalytics.revolver,
        lineItem: findCreditor(Bixby.Enums.DebtType.Revolver),
        ltm: formatCurrency(ltm.revolver, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'firstLien1',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien1,
        lineItem: findCreditor(Bixby.Enums.DebtType.FirstLienOne),
        ltm: formatCurrency(ltm.firstLien1, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'firstLien2',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien2,
        lineItem: findCreditor(Bixby.Enums.DebtType.FirstLienTwo),
        ltm: formatCurrency(ltm.firstLien2, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'firstLien3',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien3,
        lineItem: findCreditor(Bixby.Enums.DebtType.FirstLienThree),
        ltm: formatCurrency(ltm.firstLien3, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'firstLien4',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien4,
        lineItem: findCreditor(Bixby.Enums.DebtType.FirstLienFour),
        ltm: formatCurrency(ltm.firstLien4, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'firstLien5',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien5,
        lineItem: findCreditor(Bixby.Enums.DebtType.FirstLienFive),
        ltm: formatCurrency(ltm.firstLien5, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'secondLien1',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien1,
        lineItem: findCreditor(Bixby.Enums.DebtType.SecondLienOne),
        ltm: formatCurrency(ltm.secondLien1, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'secondLien2',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien2,
        lineItem: findCreditor(Bixby.Enums.DebtType.SecondLienTwo),
        ltm: formatCurrency(ltm.secondLien2, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'secondLien3',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien3,
        lineItem: findCreditor(Bixby.Enums.DebtType.SecondLienThree),
        ltm: formatCurrency(ltm.secondLien3, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'secondLien4',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien4,
        lineItem: findCreditor(Bixby.Enums.DebtType.SecondLienFour),
        ltm: formatCurrency(ltm.secondLien4, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'secondLien5',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien5,
        lineItem: findCreditor(Bixby.Enums.DebtType.SecondLienFive),
        ltm: formatCurrency(ltm.secondLien5, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'otherDebt1',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt1,
        lineItem: findCreditor(Bixby.Enums.DebtType.OtherDebtOne),
        ltm: formatCurrency(ltm.otherDebt1, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'otherDebt2',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt2,
        lineItem: findCreditor(Bixby.Enums.DebtType.OtherDebtTwo),
        ltm: formatCurrency(ltm.otherDebt2, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'otherDebt3',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt3,
        lineItem: findCreditor(Bixby.Enums.DebtType.OtherDebtThree),
        ltm: formatCurrency(ltm.otherDebt3, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'otherDebt4',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt4,
        lineItem: findCreditor(Bixby.Enums.DebtType.OtherDebtFour),
        ltm: formatCurrency(ltm.otherDebt4, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'otherDebt5',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt5,
        lineItem: findCreditor(Bixby.Enums.DebtType.OtherDebtFive),
        ltm: formatCurrency(ltm.otherDebt5, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'interestExpense',
        rowTitle: Bixby.Enums.CompanyAnalytics.interestExpense,
        lineItem: null,
        ltm: formatCurrency(ltm.interestExpense, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'lineOfCreditSize',
        rowTitle: Bixby.Enums.CompanyAnalytics.lineOfCreditSize,
        lineItem: null,
        ltm: formatCurrency(ltm.lineOfCreditSize, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
      {
        key: 'lineOfCreditAvailability',
        rowTitle: Bixby.Enums.CompanyAnalytics.lineOfCreditAvailability,
        lineItem: null,
        ltm: formatCurrency(ltm.lineOfCreditAvailability, false) || 'NM',
        priorQuarter: '',
        currentQuarter: '',
      },
    ];
  }

  public static buildAdminCapitalStructureTable(
    ltm: Bixby.Models.DraftCompanyAnalyticsLastTwelveMonths,
    priorQuarter: Bixby.Models.DraftCompanyAnalyticsPriorQuarter,
    currentQuarter: Bixby.Models.DraftCompanyAnalyticsCurrentQuarter
  ) {
    return [
      {
        key: 'date',
        rowTitle: Bixby.Enums.IncomeStatement.date,
        ltm: formatDate(ltm.date) || 'NM',
        priorQuarter: formatDate(priorQuarter.date) || 'NM',
        currentQuarter: formatDate(currentQuarter.date) || 'NM',
      },
      {
        key: 'revenue',
        rowTitle: Bixby.Enums.IncomeStatement.revenue,
        ltm: formatCurrency(ltm.revenue, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.revenue, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.revenue, false) || 'NM',
      },
      {
        key: 'revenuePercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.revenueGrowth) || 'NM',
      },
      {
        key: 'grossProfit',
        rowTitle: Bixby.Enums.IncomeStatement.grossProfit,
        ltm: formatCurrency(ltm.grossProfit, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.grossProfit, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.grossProfit, false) || 'NM',
      },
      {
        key: 'grossProfitPercentMargin',
        rowTitle: Bixby.Enums.IncomeStatement.percentMargin,
        ltm: formatPercentage(ltm.grossProfitMargin) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.grossProfitMargin) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.grossProfitMargin) || 'NM',
      },
      {
        key: 'grossProfitPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.grossProfitGrowth) || 'NM',
      },
      {
        key: 'ebit',
        rowTitle: Bixby.Enums.IncomeStatement.ebit,
        ltm: formatCurrency(ltm.ebit, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ebit, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ebit, false) || 'NM',
      },
      {
        key: 'ebitPercentMargin',
        rowTitle: Bixby.Enums.IncomeStatement.percentMargin,
        ltm: formatPercentage(ltm.ebitMargin) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.ebitMargin) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.ebitMargin) || 'NM',
      },
      {
        key: 'ebitPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.ebitGrowth) || 'NM',
      },
      {
        key: 'ebitda',
        rowTitle: Bixby.Enums.IncomeStatement.ebitda,
        ltm: formatCurrency(ltm.ebitda, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ebitda, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ebitda, false) || 'NM',
      },
      {
        key: 'ebitdaPercentMargin',
        rowTitle: Bixby.Enums.IncomeStatement.percentMargin,
        ltm: formatPercentage(ltm.ebitdaMargin) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.ebitdaMargin) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.ebitdaMargin) || 'NM',
      },
      {
        key: 'ebitdaPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.ebitdaGrowth) || 'NM',
      },
      {
        key: 'depreciationAmortization',
        rowTitle: Bixby.Enums.IncomeStatement.depreciationAmortization,
        ltm: formatCurrency(ltm.depreciationAmortization, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.depreciationAmortization, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.depreciationAmortization, false) || 'NM',
      },
      {
        key: 'ebitdaAdjustments',
        rowTitle: Bixby.Enums.IncomeStatement.ebitdaAdjustments,
        ltm: formatCurrency(ltm.ebitdaAdjustments, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ebitdaAdjustments, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ebitdaAdjustments, false) || 'NM',
      },
      {
        key: 'ebitdaAdjustmentsPercentSales',
        rowTitle: Bixby.Enums.IncomeStatement.percentOfSales,
        ltm: formatPercentage(ltm.ebitdaAdjustmentsPercentOfSales) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.ebitdaAdjustmentsPercentOfSales) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.ebitdaAdjustmentsPercentOfSales) || 'NM',
      },
      {
        key: 'ebitdaAdjustmentsPercentEbitda',
        rowTitle: Bixby.Enums.IncomeStatement.percentOfEbitda,
        ltm: formatPercentage(ltm.ebitdaAdjustmentsPercentOfEbitda) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.ebitdaAdjustmentsPercentOfEbitda) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.ebitdaAdjustmentsPercentOfEbitda) || 'NM',
      },
      {
        key: 'ebitdaAdjustmentsPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.ebitdaAdjustmentsGrowth) || 'NM',
      },
      {
        key: 'adjustedEbitda',
        rowTitle: Bixby.Enums.IncomeStatement.adjustedEbitda,
        ltm: formatCurrency(ltm.adjustedEbitda, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.adjustedEbitda, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.adjustedEbitda, false) || 'NM',
      },
      {
        key: 'adjustedEbitdaPercentMargin',
        rowTitle: Bixby.Enums.IncomeStatement.percentMargin,
        ltm: formatPercentage(ltm.adjustedEbitdaMargin) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.adjustedEbitdaMargin) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.adjustedEbitdaMargin) || 'NM',
      },
      {
        key: 'adjustedEbitdaPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.adjustedEbitdaGrowth) || 'NM',
      },
      {
        key: 'covenantEbitda',
        rowTitle: Bixby.Enums.IncomeStatement.covenantEbitda,
        ltm: formatCurrency(ltm.covenantEbitda, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.covenantEbitda, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.covenantEbitda, false) || 'NM',
      },
      {
        key: 'covenantEbitdaPercentMargin',
        rowTitle: Bixby.Enums.IncomeStatement.percentMargin,
        ltm: formatPercentage(ltm.covenantEbitdaMargin) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.covenantEbitdaMargin) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.covenantEbitdaMargin) || 'NM',
      },
      {
        key: 'covenantEbitdaPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.covenantEbitdaGrowth) || 'NM',
      },
      {
        key: 'ocf',
        rowTitle: Bixby.Enums.IncomeStatement.ocf,
        ltm: formatCurrency(ltm.ocf, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.ocf, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.ocf, false) || 'NM',
      },
      {
        key: 'ocfPercentMargin',
        rowTitle: Bixby.Enums.IncomeStatement.percentMargin,
        ltm: formatPercentage(ltm.ocfMargin) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.ocfMargin) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.ocfMargin) || 'NM',
      },
      {
        key: 'ocfPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.ocfGrowth) || 'NM',
      },
      {
        key: 'capex',
        rowTitle: Bixby.Enums.IncomeStatement.capEx,
        ltm: formatCurrency(ltm.capitalExpenditures, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.capitalExpenditures, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.capitalExpenditures, false) || 'NM',
      },
      {
        key: 'capExPercentSales',
        rowTitle: Bixby.Enums.IncomeStatement.percentOfSales,
        ltm: formatPercentage(ltm.capitalExpendituresPercentOfSales) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.capitalExpendituresPercentOfSales) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.capitalExpendituresPercentOfSales) || 'NM',
      },
      {
        key: 'ocfCapEx',
        rowTitle: Bixby.Enums.IncomeStatement.ocfCapEx,
        ltm: formatCurrency(ltm.capitalExpendituresOcfCapEx, false) || 'NM',
        priorQuarter: formatCurrency(priorQuarter.capitalExpendituresOcfCapEx, false) || 'NM',
        currentQuarter: formatCurrency(currentQuarter.capitalExpendituresOcfCapEx, false) || 'NM',
      },
      {
        key: 'ocfCapExPercentMargin',
        rowTitle: Bixby.Enums.IncomeStatement.percentMargin,
        ltm: formatPercentage(ltm.capitalExpendituresMargin) || 'NM',
        priorQuarter: formatPercentage(priorQuarter.capitalExpendituresMargin) || 'NM',
        currentQuarter: formatPercentage(currentQuarter.capitalExpendituresMargin) || 'NM',
      },
      {
        key: 'ocfCapExPercentGrowth',
        rowTitle: Bixby.Enums.IncomeStatement.percentGrowth,
        ltm: '',
        priorQuarter: '',
        currentQuarter: formatPercentage(currentQuarter.capitalExpendituresGrowth) || 'NM',
      },
    ] as Bixby.Models.AdminIncomeStatementGridRow[];
  }

  public static buildAdminLeverageStatsTable(ltm: Bixby.Models.DraftCompanyAnalyticsLastTwelveMonths) {
    return [
      {
        key: 'date',
        rowTitle: Bixby.Enums.CompanyAnalytics.date,
        value: formatDate(ltm.date),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'cash',
        rowTitle: Bixby.Enums.CompanyAnalytics.cash,
        value: formatCurrency(ltm.cash, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'abl',
        rowTitle: Bixby.Enums.CompanyAnalytics.abl,
        value: formatCurrency(ltm.abl, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'priorityDebt',
        rowTitle: Bixby.Enums.CompanyAnalytics.priorityDebt,
        value: formatCurrency(ltm.priorityDebt, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'revolver',
        rowTitle: Bixby.Enums.CompanyAnalytics.revolver,
        value: ltm.revolver === null ? '$0' : formatCurrency(ltm.revolver, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'firstLien1',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien1,
        value: formatCurrency(ltm.firstLien1, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'firstLien2',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien2,
        value: formatCurrency(ltm.firstLien2, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'firstLien3',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien3,
        value: formatCurrency(ltm.firstLien3, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'firstLien4',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien4,
        value: formatCurrency(ltm.firstLien4, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'firstLien5',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLien5,
        value: formatCurrency(ltm.firstLien5, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'firstLienDebtGross',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLienDebtGross,
        value: formatCurrency(ltm.firstLienDebtGross, false),
        ebitda: formatMultiplier(ltm.firstLienDebtGrossEbitda),
        adjustedEbitda: formatMultiplier(ltm.firstLienDebtGrossAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.firstLienDebtGrossCovenantEbitda),
      },
      {
        key: 'firstLienDebtNet',
        rowTitle: Bixby.Enums.CompanyAnalytics.firstLienDebtNet,
        value: formatCurrency(ltm.firstLienDebtNet, false),
        ebitda: formatMultiplier(ltm.firstLienDebtNetEbitda),
        adjustedEbitda: formatMultiplier(ltm.firstLienDebtNetAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.firstLienDebtNetCovenantEbitda),
      },
      {
        key: 'secondLien1',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien1,
        value: formatCurrency(ltm.secondLien1, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'secondLien2',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien2,
        value: formatCurrency(ltm.secondLien2, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'secondLien3',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien3,
        value: formatCurrency(ltm.secondLien3, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'secondLien4',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien4,
        value: formatCurrency(ltm.secondLien4, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'secondLien5',
        rowTitle: Bixby.Enums.CompanyAnalytics.secondLien5,
        value: formatCurrency(ltm.secondLien5, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'securedDebtGross',
        rowTitle: Bixby.Enums.CompanyAnalytics.securedLienDebtGross,
        value: formatCurrency(ltm.securedDebtGross, false),
        ebitda: formatMultiplier(ltm.securedDebtGrossEbitda),
        adjustedEbitda: formatMultiplier(ltm.securedDebtGrossAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.securedDebtGrossCovenantEbitda),
      },
      {
        key: 'securedDebtNet',
        rowTitle: Bixby.Enums.CompanyAnalytics.securedLienDebtNet,
        value: formatCurrency(ltm.securedDebtNet, false),
        ebitda: formatMultiplier(ltm.securedDebtNetEbitda),
        adjustedEbitda: formatMultiplier(ltm.securedDebtNetAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.securedDebtNetCovenantEbitda),
      },
      {
        key: 'otherDebt1',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt1,
        value: formatCurrency(ltm.otherDebt1, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'otherDebt2',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt2,
        value: formatCurrency(ltm.otherDebt2, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'otherDebt3',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt3,
        value: formatCurrency(ltm.otherDebt3, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'otherDebt4',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt4,
        value: formatCurrency(ltm.otherDebt4, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'otherDebt5',
        rowTitle: Bixby.Enums.CompanyAnalytics.otherDebt5,
        value: formatCurrency(ltm.otherDebt5, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'totalDebtGross',
        rowTitle: Bixby.Enums.CompanyAnalytics.totalDebtGross,
        value: formatCurrency(ltm.totalDebtGross, false),
        ebitda: formatMultiplier(ltm.totalDebtGrossEbitda),
        adjustedEbitda: formatMultiplier(ltm.totalDebtGrossAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.totalDebtGrossCovenantEbitda),
      },
      {
        key: 'totalDebtNet',
        rowTitle: Bixby.Enums.CompanyAnalytics.totalDebtNet,
        value: formatCurrency(ltm.totalDebtNet, false),
        ebitda: formatMultiplier(ltm.totalDebtNetEbitda),
        adjustedEbitda: formatMultiplier(ltm.totalDebtNetAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.totalDebtNetCovenantEbitda),
      },
      {
        key: 'breakline1',
        rowTitle: null,
        value: null,
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'interestExpense',
        rowTitle: Bixby.Enums.CompanyAnalytics.interestExpense,
        value: formatCurrency(ltm.interestExpense, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'interestCoverageEbitda',
        rowTitle: Bixby.Enums.CompanyAnalytics.interestCoverage,
        value: null,
        ebitda: formatMultiplier(ltm.interestCoverageEbitda),
        adjustedEbitda: formatMultiplier(ltm.interestCoverageAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.interestCoverageCovenantEbitda),
      },
      {
        key: 'interestCoverageAfterCapitalExpendituresEbitda',
        rowTitle: Bixby.Enums.CompanyAnalytics.interestCoverageAfterCapEx,
        value: null,
        ebitda: formatMultiplier(ltm.interestCoverageAfterCapitalExpendituresEbitda),
        adjustedEbitda: formatMultiplier(ltm.interestCoverageAfterCapitalExpendituresAdjustedEbitda),
        covenantEbitda: formatMultiplier(ltm.interestCoverageAfterCapitalExpendituresCovenantEbitda),
      },
      {
        key: 'lineOfCreditSize',
        rowTitle: Bixby.Enums.CompanyAnalytics.lineOfCreditSize,
        value: formatCurrency(ltm.lineOfCreditSize, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'lineOfCreditAvailability',
        rowTitle: Bixby.Enums.CompanyAnalytics.lineOfCreditAvailability,
        value: ltm.lineOfCreditAvailability === null ? '$0' : formatCurrency(ltm.lineOfCreditAvailability, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
      {
        key: 'availableLiquidity',
        rowTitle: Bixby.Enums.CompanyAnalytics.availableLiquidity,
        value: formatCurrency(ltm.availableLiquidity, false),
        ebitda: null,
        adjustedEbitda: null,
        covenantEbitda: null,
      },
    ] as Bixby.Models.AdminLeverageStatsGridRow[];
  }

  public static convertTableToCompanyLoanDetails(rows: Bixby.Models.DraftCompanyLoan[]) {
    const creditors: Bixby.Models.DraftCompanyCreditor[] = rows.map(
      r => ({ name: r.name, debtType: r.debtType } as any)
    );
    const loans: Bixby.Models.DraftCompanyLoan[] = rows.map(r => ({
      debtType: r.debtType as any,
      spread: r.spread ? r.spread : null,
      maturity: formatDate(r.maturity as any) as any,
      agent: r.agent ? r.agent : null,
      covenantLite: r.covenantLite && (r.covenantLite as string) !== 'NM' ? r.covenantLite : null,
    }));

    return { creditors, loans };
  }

  public static convertTableToCompanyTransactionDetails(
    rows: Bixby.Models.AdminTransactionDetailGridRow[],
    isVisible: boolean,
    notes: string | null
  ): Bixby.Models.DraftCompanyTransactionDetails {
    const getTransactionDateDetails = () => {
      const row = Bixby.Utils.find(rows, r => r.key === Bixby.Enums.CompanyTransactionDetailsKeys.TransactionDate);
      if (!row) return;
      const value = formatDate(row.value as any) as any;
      return {
        name: row.name,
        value: value,
      };
    };

    const getTransactionDetails = (key: Bixby.Enums.CompanyTransactionDetailsKeys) => {
      const row = Bixby.Utils.find(rows, r => r.key === key);
      if (!row) return;

      let value: string | null = formatCurrency(
        row.value
          ? (row.value
              .toString()
              .replace(/\$/g, '')
              .replace(/,/g, '')
              .replace(/\(/g, '-')
              .replace(/\)/g, '')
              .replace(/\s/g, '') as any)
          : null,
        true
      );

      if (!value || value === 'NM' || value === 'NA') {
        value = null;
      } else {
        value = value.replace(/\$/g, '').replace(/,/g, '').replace(/\(/g, '-').replace(/\)/g, '');
      }

      return {
        name: row.name,
        value: value,
      };
    };

    const getPercentage = (value: string | number | null | undefined): number | null => {
      if (value === null || value === undefined) {
        return null;
      }

      let parsedValue: number = parseFloat(value as any);
      if (isNaN(parsedValue)) {
        return null;
      }

      parsedValue = parsedValue / 100;

      return parsedValue;
    };

    const transactionDate = getTransactionDateDetails();
    const totalEnterpriseValue = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.TotalEnterpriseValue);
    const revenueAtBuyout = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.RevenueAtBuyout);
    const managementAdjustedEbitdaAtBuyout = getTransactionDetails(
      Bixby.Enums.CompanyTransactionDetailsKeys.ManagementAdjustedEbitdaAtBuyout
    );
    const totalEnterpriseValueByRevenue = getTransactionDetails(
      Bixby.Enums.CompanyTransactionDetailsKeys.TotalEnterpriseValueByRevenue
    );
    const totalEnterpriseValueByAdjustedManagementEbitda = getTransactionDetails(
      Bixby.Enums.CompanyTransactionDetailsKeys.TotalEnterpriseValueByAdjustedManagementEbitda
    );
    const netDebt = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.NetDebt);
    const netDebtPercentageOfCap = getTransactionDetails(
      Bixby.Enums.CompanyTransactionDetailsKeys.NetDebtPercentageOfCap
    );
    const sponsorEquity = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.SponsorEquity);
    const rolloverEquity = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.RolloverEquity);
    const totalEquity = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.TotalEquity);
    const totalEquityPercentageOfCap = getTransactionDetails(
      Bixby.Enums.CompanyTransactionDetailsKeys.TotalEquityPercentageOfCap
    );
    const cumulativeDividends = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.CumulativeDividends);
    const impliedEquityAtRisk = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.ImpliedEquityAtRisk);

    const securedDebt = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.SecuredDebt);
    const cash = getTransactionDetails(Bixby.Enums.CompanyTransactionDetailsKeys.Cash);

    return {
      transactionDate: transactionDate?.value,
      transactionDateName: transactionDate?.name,
      totalEnterpriseValue: totalEnterpriseValue?.value,
      totalEnterpriseValueName: totalEnterpriseValue?.name,
      revenueAtBuyout: revenueAtBuyout?.value,
      revenueAtBuyoutName: revenueAtBuyout?.name,
      managementAdjustedEbitdaAtBuyout: managementAdjustedEbitdaAtBuyout?.value,
      managementAdjustedEbitdaAtBuyoutName: managementAdjustedEbitdaAtBuyout?.name,
      totalEnterpriseValueByRevenue: totalEnterpriseValueByRevenue?.value,
      totalEnterpriseValueByRevenueName: totalEnterpriseValueByRevenue?.name,
      totalEnterpriseValueByAdjustedManagementEbitda: totalEnterpriseValueByAdjustedManagementEbitda?.value,
      totalEnterpriseValueByAdjustedManagementEbitdaName: totalEnterpriseValueByAdjustedManagementEbitda?.name,
      netDebt: netDebt?.value,
      netDebtName: netDebt?.name,
      netDebtPercentageOfCap: getPercentage(netDebtPercentageOfCap?.value),
      netDebtPercentageOfCapName: netDebtPercentageOfCap?.name,
      sponsorEquity: sponsorEquity?.value,
      sponsorEquityName: sponsorEquity?.name,
      rolloverEquity: rolloverEquity?.value,
      rolloverEquityName: rolloverEquity?.name,
      totalEquity: totalEquity?.value,
      totalEquityName: totalEquity?.name,
      totalEquityPercentageOfCap: getPercentage(totalEquityPercentageOfCap?.value),
      totalEquityPercentageOfCapName: totalEquityPercentageOfCap?.name,
      cumulativeDividends: cumulativeDividends?.value,
      cumulativeDividendsName: cumulativeDividends?.name,
      impliedEquityAtRisk: impliedEquityAtRisk?.value,
      impliedEquityAtRiskName: impliedEquityAtRisk?.name,
      notes: notes,
      isVisible: isVisible,
      securedDebt: securedDebt?.value,
      securedDebtName: securedDebt?.name,
      cash: cash?.value,
      cashName: cash?.name,
    } as Bixby.Models.DraftCompanyTransactionDetails;
  }

  public static convertTableToCompanyAnalytics(rows: Bixby.Models.AdminIncomeStatementStatGridRow[]) {
    const mapDraftCompanyAnalyticsBase = (
      getValue: (row: Bixby.Models.AdminIncomeStatementStatGridRow | undefined) => Date | number | string | null
    ) => {
      const findRawValue = (key: keyof Bixby.Models.DraftCompanyAnalyticsBase) =>
        getValue(Bixby.Utils.find(rows, r => r.key === key));
      const findDateValue = (key: keyof Bixby.Models.DraftCompanyAnalyticsBase) => formatDate(findRawValue(key) as any);
      const findValue = (key: keyof Bixby.Models.DraftCompanyAnalyticsBase) => {
        const rawValue: string | number | Date | null = findRawValue(key);
        const currencyString: string | null = formatCurrency(
          rawValue
            ? (rawValue
                .toString()
                .replace(/\$/g, '')
                .replace(/,/g, '')
                .replace(/\(/g, '-')
                .replace(/\)/g, '')
                .replace(/\s/g, '') as any)
            : null,
          true
        );

        if (!currencyString || currencyString === 'NM') {
          return null;
        } else {
          return currencyString.replace(/\$/g, '').replace(/,/g, '').replace(/\(/g, '-').replace(/\)/g, '');
        }
      };

      return {
        date: findDateValue('date') as any,
        revenue: findValue('revenue'),
        grossProfit: findValue('grossProfit'),
        ebit: findValue('ebit'),
        ebitda: findValue('ebitda'),
        ebitdaAdjustments: findValue('ebitdaAdjustments'),
        adjustedEbitda: findValue('adjustedEbitda'),
        covenantEbitda: findValue('covenantEbitda'),
        ocf: findValue('ocf'),
        capitalExpenditures: findValue('capitalExpenditures'),
        grossProfitMargin: findValue('grossProfitMargin'),
        ebitMargin: findValue('ebitMargin'),
        ebitdaMargin: findValue('ebitdaMargin'),
        ebitdaAdjustmentsPercentOfSales: findValue('ebitdaAdjustmentsPercentOfSales'),
        ebitdaAdjustmentsPercentOfEbitda: findValue('ebitdaAdjustmentsPercentOfEbitda'),
        adjustedEbitdaMargin: findValue('adjustedEbitdaMargin'),
        ocfMargin: findValue('ocfMargin'),
        capitalExpendituresPercentOfSales: findValue('capitalExpendituresPercentOfSales'),
        capitalExpendituresOcfCapEx: findValue('capitalExpendituresOcfCapEx'),
        capitalExpendituresMargin: findValue('capitalExpendituresMargin'),
      } as Bixby.Models.DraftCompanyAnalyticsBase;
    };
    const mapDraftCompanyAnalyticsCurrentQuarter = () => {
      const getValue = (row: Bixby.Models.AdminIncomeStatementStatGridRow | undefined) =>
        !row ? null : row.currentQuarter;
      const findValue = (key: keyof Bixby.Models.DraftCompanyAnalyticsCurrentQuarter) => {
        const rawValue: string | number | Date | null = getValue(Bixby.Utils.find(rows, r => r.key === key));
        const currencyString: string | null = formatCurrency(
          rawValue
            ? (rawValue
                .toString()
                .replace(/\$/g, '')
                .replace(/,/g, '')
                .replace(/\(/g, '-')
                .replace(/\)/g, '')
                .replace(/\s/g, '') as any)
            : null,
          true
        );

        if (!currencyString || currencyString === 'NM') {
          return null;
        } else {
          return currencyString.replace(/\$/g, '').replace(/,/g, '').replace(/\(/g, '-').replace(/\)/g, '');
        }
      };

      return Object.assign({}, mapDraftCompanyAnalyticsBase(getValue), {
        revenueGrowth: findValue('revenueGrowth'),
        grossProfitGrowth: findValue('grossProfitGrowth'),
        ebitGrowth: findValue('ebitGrowth'),
        ebitdaGrowth: findValue('ebitdaGrowth'),
        ebitdaAdjustmentsGrowth: findValue('ebitdaAdjustmentsGrowth'),
        adjustedEbitdaGrowth: findValue('adjustedEbitdaGrowth'),
        ocfGrowth: findValue('ocfGrowth'),
        capitalExpendituresGrowth: findValue('capitalExpendituresGrowth'),
      }) as Bixby.Models.DraftCompanyAnalyticsCurrentQuarter;
    };
    const mapDraftCompanyAnalyticsLastTwelveMonths = () => {
      const getValue = (row: Bixby.Models.AdminIncomeStatementStatGridRow | undefined) => (!row ? null : row.ltm);
      const findRawValue = (key: keyof Bixby.Models.DraftCompanyAnalyticsLastTwelveMonths) =>
        getValue(Bixby.Utils.find(rows, r => r.key === key));
      const findValue = (key: keyof Bixby.Models.DraftCompanyAnalyticsLastTwelveMonths) => {
        const rawValue: string | number | Date | null = findRawValue(key);
        const currencyString: string | null = formatCurrency(
          rawValue
            ? (rawValue
                .toString()
                .replace(/\$/g, '')
                .replace(/,/g, '')
                .replace(/\(/g, '-')
                .replace(/\)/g, '')
                .replace(/\s/g, '') as any)
            : null,
          true
        );

        if (!currencyString || currencyString === 'NM') {
          return null;
        } else {
          return currencyString.replace(/\$/g, '').replace(/,/g, '').replace(/\(/g, '-').replace(/\)/g, '');
        }
      };

      return Object.assign({}, mapDraftCompanyAnalyticsBase(getValue), {
        cash: findValue('cash'),
        abl: findValue('abl'),
        priorityDebt: findValue('priorityDebt'),
        revolver: findValue('revolver'),
        firstLien1: findValue('firstLien1'),
        firstLien2: findValue('firstLien2'),
        firstLien3: findValue('firstLien3'),
        firstLien4: findValue('firstLien4'),
        firstLien5: findValue('firstLien5'),
        secondLien1: findValue('secondLien1'),
        secondLien2: findValue('secondLien2'),
        secondLien3: findValue('secondLien3'),
        secondLien4: findValue('secondLien4'),
        secondLien5: findValue('secondLien5'),
        otherDebt1: findValue('otherDebt1'),
        otherDebt2: findValue('otherDebt2'),
        otherDebt3: findValue('otherDebt3'),
        otherDebt4: findValue('otherDebt4'),
        otherDebt5: findValue('otherDebt5'),
        interestExpense: findValue('interestExpense'),
        lineOfCreditSize: findValue('lineOfCreditSize'),
        lineOfCreditAvailability: findValue('lineOfCreditAvailability'),
        firstLienDebtGross: findValue('firstLienDebtGross'),
        firstLienDebtGrossAdjustedEbitda: findValue('firstLienDebtGrossAdjustedEbitda'),
        firstLienDebtGrossCovenantEbitda: findValue('firstLienDebtGrossCovenantEbitda'),
        firstLienDebtNet: findValue('firstLienDebtNet'),
        firstLienDebtNetEbitda: findValue('firstLienDebtNetEbitda'),
        firstLienDebtNetAdjustedEbitda: findValue('firstLienDebtNetAdjustedEbitda'),
        firstLienDebtNetCovenantEbitda: findValue('firstLienDebtNetCovenantEbitda'),
        securedDebtGross: findValue('securedDebtGross'),
        securedDebtGrossEbitda: findValue('securedDebtGrossEbitda'),
        securedDebtGrossAdjustedEbitda: findValue('securedDebtGrossAdjustedEbitda'),
        securedDebtGrossCovenantEbitda: findValue('securedDebtGrossCovenantEbitda'),
        securedDebtNet: findValue('securedDebtNet'),
        securedDebtNetEbitda: findValue('securedDebtNetEbitda'),
        securedDebtNetAdjustedEbitda: findValue('securedDebtNetAdjustedEbitda'),
        securedDebtNetCovenantEbitda: findValue('securedDebtNetCovenantEbitda'),
        totalDebtGross: findValue('totalDebtGross'),
        totalDebtGrossEbitda: findValue('totalDebtGrossEbitda'),
        totalDebtGrossAdjustedEbitda: findValue('totalDebtGrossAdjustedEbitda'),
        totalDebtGrossCovenantEbitda: findValue('totalDebtGrossCovenantEbitda'),
        totalDebtNet: findValue('totalDebtNet'),
        totalDebtNetEbitda: findValue('totalDebtNetEbitda'),
        totalDebtNetAdjustedEbitda: findValue('totalDebtNetAdjustedEbitda'),
        totalDebtNetCovenantEbitda: findValue('totalDebtNetCovenantEbitda'),
        interestCoverageEbitda: findValue('interestCoverageEbitda'),
        interestCoverageAdjustedEbitda: findValue('interestCoverageAdjustedEbitda'),
        interestCoverageCovenantEbitda: findValue('interestCoverageCovenantEbitda'),
        interestCoverageAfterCapitalExpendituresEbitda: findValue('interestCoverageAfterCapitalExpendituresEbitda'),
        interestCoverageAfterCapitalExpendituresAdjustedEbitda: findValue(
          'interestCoverageAfterCapitalExpendituresAdjustedEbitda'
        ),
        interestCoverageAfterCapitalExpendituresCovenantEbitda: findValue(
          'interestCoverageAfterCapitalExpendituresCovenantEbitda'
        ),
        availableLiquidity: findValue('availableLiquidity'),
      }) as Bixby.Models.DraftCompanyAnalyticsLastTwelveMonths;
    };
    const mapDraftCompanyAnalyticsPriorQuarter = () => {
      const getValue = (row: Bixby.Models.AdminIncomeStatementStatGridRow | undefined) =>
        !row ? null : row.priorQuarter;

      return Object.assign(
        {},
        mapDraftCompanyAnalyticsBase(getValue)
      ) as Bixby.Models.DraftCompanyAnalyticsPriorQuarter;
    };

    const analyticsLastTwelveMonths: Bixby.Models.DraftCompanyAnalyticsLastTwelveMonths =
      mapDraftCompanyAnalyticsLastTwelveMonths();
    const analyticsPriorQuarter: Bixby.Models.DraftCompanyAnalyticsPriorQuarter =
      mapDraftCompanyAnalyticsPriorQuarter();
    const analyticsCurrentQuarter: Bixby.Models.DraftCompanyAnalyticsCurrentQuarter =
      mapDraftCompanyAnalyticsCurrentQuarter();

    const calculatePercentGrowth = (numerator: number | null | undefined, denominator: number | null | undefined) => {
      if (
        numerator === null ||
        numerator === undefined ||
        denominator === null ||
        denominator === undefined ||
        denominator === 0
      ) {
        return null;
      } else {
        try {
          const mrq = parseFloat(numerator as any);
          const lyq = parseFloat(denominator as any);

          let result: number | null = null;

          if (mrq < 0 && lyq < 0) {
            result = Math.abs(mrq - lyq) / lyq;
          } else if ((mrq < 0 && lyq > 0) || (mrq > 0 && lyq < 0)) {
            // NM if one is positive and one is negative
            result = null;
          } else {
            result = mrq / lyq - 1;
          }

          // NM if result is +/- 100%
          if (result !== null && (result > 1 || result < -1)) {
            result = null;
          }

          return result === null ? null : (result.toFixed(3) as any);
        } catch (e: any) {
          // NM for errors
          return null;
        }
      }
    };

    const calculatePercentMargin = (numerator: number | null | undefined, denominator: number | null | undefined) => {
      if (
        numerator === null ||
        numerator === undefined ||
        denominator === null ||
        denominator === undefined ||
        denominator === 0
      ) {
        return null;
      } else {
        try {
          let result: number | null = parseFloat(numerator as any) / parseFloat(denominator as any);

          // NM if result is +/- 100%
          if (result !== null && (result > 1 || result < -1)) {
            result = null;
          }

          return result === null ? null : (result.toFixed(3) as any);
        } catch (e: any) {
          // NM for errors
          return null;
        }
      }
    };

    const calculateDiff = (left: number | null | undefined, right: number | null | undefined) => {
      if (left === null || left === undefined || right === null || right === undefined) {
        return null;
      } else {
        return (parseFloat(left as any) - parseFloat(right as any)).toFixed(2) as any;
      }
    };

    // Revenue
    analyticsCurrentQuarter.revenueGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.revenue,
      analyticsPriorQuarter.revenue
    );

    /// Gross Profit
    analyticsLastTwelveMonths.grossProfitMargin = calculatePercentMargin(
      analyticsLastTwelveMonths.grossProfit,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.grossProfitMargin = calculatePercentMargin(
      analyticsPriorQuarter.grossProfit,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.grossProfitMargin = calculatePercentMargin(
      analyticsCurrentQuarter.grossProfit,
      analyticsCurrentQuarter.revenue
    );
    analyticsCurrentQuarter.grossProfitGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.grossProfit,
      analyticsPriorQuarter.grossProfit
    );

    // EBIT
    analyticsLastTwelveMonths.ebitMargin = calculatePercentMargin(
      analyticsLastTwelveMonths.ebit,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.ebitMargin = calculatePercentMargin(
      analyticsPriorQuarter.ebit,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.ebitMargin = calculatePercentMargin(
      analyticsCurrentQuarter.ebit,
      analyticsCurrentQuarter.revenue
    );
    analyticsCurrentQuarter.ebitGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.ebit,
      analyticsPriorQuarter.ebit
    );

    // EBITDA
    analyticsLastTwelveMonths.ebitdaMargin = calculatePercentMargin(
      analyticsLastTwelveMonths.ebitda,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.ebitdaMargin = calculatePercentMargin(
      analyticsPriorQuarter.ebitda,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.ebitdaMargin = calculatePercentMargin(
      analyticsCurrentQuarter.ebitda,
      analyticsCurrentQuarter.revenue
    );
    analyticsCurrentQuarter.ebitdaGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.ebitda,
      analyticsPriorQuarter.ebitda
    );

    // Depreciation and Amortization
    analyticsLastTwelveMonths.depreciationAmortization = calculateDiff(
      analyticsLastTwelveMonths.ebitda,
      analyticsLastTwelveMonths.ebit
    );
    analyticsPriorQuarter.depreciationAmortization = calculateDiff(
      analyticsPriorQuarter.ebitda,
      analyticsPriorQuarter.ebit
    );
    analyticsCurrentQuarter.depreciationAmortization = calculateDiff(
      analyticsCurrentQuarter.ebitda,
      analyticsCurrentQuarter.ebit
    );

    // EBITDA Adjustments
    analyticsLastTwelveMonths.ebitdaAdjustmentsPercentOfSales = calculatePercentMargin(
      analyticsLastTwelveMonths.ebitdaAdjustments,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.ebitdaAdjustmentsPercentOfSales = calculatePercentMargin(
      analyticsPriorQuarter.ebitdaAdjustments,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.ebitdaAdjustmentsPercentOfSales = calculatePercentMargin(
      analyticsCurrentQuarter.ebitdaAdjustments,
      analyticsCurrentQuarter.revenue
    );
    analyticsLastTwelveMonths.ebitdaAdjustmentsPercentOfEbitda = calculatePercentMargin(
      analyticsLastTwelveMonths.ebitdaAdjustments,
      analyticsLastTwelveMonths.ebitda
    );
    analyticsPriorQuarter.ebitdaAdjustmentsPercentOfEbitda = calculatePercentMargin(
      analyticsPriorQuarter.ebitdaAdjustments,
      analyticsPriorQuarter.ebitda
    );
    analyticsCurrentQuarter.ebitdaAdjustmentsPercentOfEbitda = calculatePercentMargin(
      analyticsCurrentQuarter.ebitdaAdjustments,
      analyticsCurrentQuarter.ebitda
    );
    analyticsCurrentQuarter.ebitdaAdjustmentsGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.ebitdaAdjustments,
      analyticsPriorQuarter.ebitdaAdjustments
    );

    // Adjusted EBITDA
    analyticsLastTwelveMonths.adjustedEbitdaMargin = calculatePercentMargin(
      analyticsLastTwelveMonths.adjustedEbitda,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.adjustedEbitdaMargin = calculatePercentMargin(
      analyticsPriorQuarter.adjustedEbitda,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.adjustedEbitdaMargin = calculatePercentMargin(
      analyticsCurrentQuarter.adjustedEbitda,
      analyticsCurrentQuarter.revenue
    );
    analyticsCurrentQuarter.adjustedEbitdaGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.adjustedEbitda,
      analyticsPriorQuarter.adjustedEbitda
    );

    // Covenant EBITDA
    analyticsLastTwelveMonths.covenantEbitdaMargin = calculatePercentMargin(
      analyticsLastTwelveMonths.covenantEbitda,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.covenantEbitdaMargin = calculatePercentMargin(
      analyticsPriorQuarter.covenantEbitda,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.covenantEbitdaMargin = calculatePercentMargin(
      analyticsCurrentQuarter.covenantEbitda,
      analyticsCurrentQuarter.revenue
    );
    analyticsCurrentQuarter.covenantEbitdaGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.covenantEbitda,
      analyticsPriorQuarter.covenantEbitda
    );

    // OCF
    analyticsLastTwelveMonths.ocfMargin = calculatePercentMargin(
      analyticsLastTwelveMonths.ocf,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.ocfMargin = calculatePercentMargin(analyticsPriorQuarter.ocf, analyticsPriorQuarter.revenue);
    analyticsCurrentQuarter.ocfMargin = calculatePercentMargin(
      analyticsCurrentQuarter.ocf,
      analyticsCurrentQuarter.revenue
    );
    analyticsCurrentQuarter.ocfGrowth = calculatePercentGrowth(analyticsCurrentQuarter.ocf, analyticsPriorQuarter.ocf);

    // Capital Expenditures (CapEx)
    analyticsLastTwelveMonths.capitalExpendituresPercentOfSales = calculatePercentMargin(
      analyticsLastTwelveMonths.capitalExpenditures,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.capitalExpendituresPercentOfSales = calculatePercentMargin(
      analyticsPriorQuarter.capitalExpenditures,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.capitalExpendituresPercentOfSales = calculatePercentMargin(
      analyticsCurrentQuarter.capitalExpenditures,
      analyticsCurrentQuarter.revenue
    );

    // OCF-CapEx
    analyticsLastTwelveMonths.capitalExpendituresOcfCapEx = calculateDiff(
      analyticsLastTwelveMonths.ocf,
      analyticsLastTwelveMonths.capitalExpenditures
    );
    analyticsPriorQuarter.capitalExpendituresOcfCapEx = calculateDiff(
      analyticsPriorQuarter.ocf,
      analyticsPriorQuarter.capitalExpenditures
    );
    analyticsCurrentQuarter.capitalExpendituresOcfCapEx = calculateDiff(
      analyticsCurrentQuarter.ocf,
      analyticsCurrentQuarter.capitalExpenditures
    );
    analyticsLastTwelveMonths.capitalExpendituresMargin = calculatePercentMargin(
      analyticsLastTwelveMonths.capitalExpendituresOcfCapEx,
      analyticsLastTwelveMonths.revenue
    );
    analyticsPriorQuarter.capitalExpendituresMargin = calculatePercentMargin(
      analyticsPriorQuarter.capitalExpendituresOcfCapEx,
      analyticsPriorQuarter.revenue
    );
    analyticsCurrentQuarter.capitalExpendituresMargin = calculatePercentMargin(
      analyticsCurrentQuarter.capitalExpendituresOcfCapEx,
      analyticsCurrentQuarter.revenue
    );
    analyticsCurrentQuarter.capitalExpendituresGrowth = calculatePercentGrowth(
      analyticsCurrentQuarter.capitalExpendituresOcfCapEx,
      analyticsPriorQuarter.capitalExpendituresOcfCapEx
    );

    // 1st Lien Debt
    analyticsLastTwelveMonths.firstLienDebtGross = (
      parseFloat((analyticsLastTwelveMonths.abl as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.priorityDebt as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.revolver as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.firstLien1 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.firstLien2 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.firstLien3 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.firstLien4 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.firstLien5 as any) || '0')
    ).toFixed(2) as any;
    analyticsLastTwelveMonths.firstLienDebtGrossEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.firstLienDebtGross,
      analyticsLastTwelveMonths.ebitda
    );
    analyticsLastTwelveMonths.firstLienDebtGrossAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.firstLienDebtGross,
      analyticsLastTwelveMonths.adjustedEbitda
    );
    analyticsLastTwelveMonths.firstLienDebtGrossCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.firstLienDebtGross,
      analyticsLastTwelveMonths.covenantEbitda
    );
    analyticsLastTwelveMonths.firstLienDebtNet = calculateDiff(
      analyticsLastTwelveMonths.firstLienDebtGross,
      analyticsLastTwelveMonths.cash
    );
    analyticsLastTwelveMonths.firstLienDebtNetEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.firstLienDebtNet,
      analyticsLastTwelveMonths.ebitda
    );
    analyticsLastTwelveMonths.firstLienDebtNetAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.firstLienDebtNet,
      analyticsLastTwelveMonths.adjustedEbitda
    );
    analyticsLastTwelveMonths.firstLienDebtNetCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.firstLienDebtNet,
      analyticsLastTwelveMonths.covenantEbitda
    );

    // 2nd Lien Debt
    analyticsLastTwelveMonths.securedDebtGross = (
      parseFloat((analyticsLastTwelveMonths.firstLienDebtGross as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.secondLien1 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.secondLien2 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.secondLien3 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.secondLien4 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.secondLien5 as any) || '0')
    ).toFixed(2) as any;
    analyticsLastTwelveMonths.securedDebtGrossEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.securedDebtGross,
      analyticsLastTwelveMonths.ebitda
    );
    analyticsLastTwelveMonths.securedDebtGrossAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.securedDebtGross,
      analyticsLastTwelveMonths.adjustedEbitda
    );
    analyticsLastTwelveMonths.securedDebtGrossCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.securedDebtGross,
      analyticsLastTwelveMonths.covenantEbitda
    );
    analyticsLastTwelveMonths.securedDebtNet = calculateDiff(
      analyticsLastTwelveMonths.securedDebtGross,
      analyticsLastTwelveMonths.cash
    );
    analyticsLastTwelveMonths.securedDebtNetEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.securedDebtNet,
      analyticsLastTwelveMonths.ebitda
    );
    analyticsLastTwelveMonths.securedDebtNetAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.securedDebtNet,
      analyticsLastTwelveMonths.adjustedEbitda
    );
    analyticsLastTwelveMonths.securedDebtNetCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.securedDebtNet,
      analyticsLastTwelveMonths.covenantEbitda
    );

    // Other Debt
    analyticsLastTwelveMonths.totalDebtGross = (
      parseFloat((analyticsLastTwelveMonths.securedDebtGross as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.otherDebt1 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.otherDebt2 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.otherDebt3 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.otherDebt4 as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.otherDebt5 as any) || '0')
    ).toFixed(2) as any;
    analyticsLastTwelveMonths.totalDebtGrossEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.totalDebtGross,
      analyticsLastTwelveMonths.ebitda
    );
    analyticsLastTwelveMonths.totalDebtGrossAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.totalDebtGross,
      analyticsLastTwelveMonths.adjustedEbitda
    );
    analyticsLastTwelveMonths.totalDebtGrossCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.totalDebtGross,
      analyticsLastTwelveMonths.covenantEbitda
    );
    analyticsLastTwelveMonths.totalDebtNet = calculateDiff(
      analyticsLastTwelveMonths.totalDebtGross,
      analyticsLastTwelveMonths.cash
    );
    analyticsLastTwelveMonths.totalDebtNetEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.totalDebtNet,
      analyticsLastTwelveMonths.ebitda
    );
    analyticsLastTwelveMonths.totalDebtNetAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.totalDebtNet,
      analyticsLastTwelveMonths.adjustedEbitda
    );
    analyticsLastTwelveMonths.totalDebtNetCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.totalDebtNet,
      analyticsLastTwelveMonths.covenantEbitda
    );

    // Interest
    analyticsLastTwelveMonths.interestCoverageEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.ebitda,
      analyticsLastTwelveMonths.interestExpense
    );
    analyticsLastTwelveMonths.interestCoverageAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.adjustedEbitda,
      analyticsLastTwelveMonths.interestExpense
    );
    analyticsLastTwelveMonths.interestCoverageCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.covenantEbitda,
      analyticsLastTwelveMonths.interestExpense
    );

    const capitalExpenditures: number = parseFloat((analyticsLastTwelveMonths.capitalExpenditures as any) || '0');
    analyticsLastTwelveMonths.interestCoverageAfterCapitalExpendituresEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.ebitda == null || analyticsLastTwelveMonths.ebitda == undefined
        ? null
        : parseFloat((analyticsLastTwelveMonths.ebitda as any) || '0') - capitalExpenditures,
      analyticsLastTwelveMonths.interestExpense
    );
    analyticsLastTwelveMonths.interestCoverageAfterCapitalExpendituresAdjustedEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.adjustedEbitda == null || analyticsLastTwelveMonths.adjustedEbitda == undefined
        ? null
        : parseFloat((analyticsLastTwelveMonths.adjustedEbitda as any) || '0') - capitalExpenditures,
      analyticsLastTwelveMonths.interestExpense
    );
    analyticsLastTwelveMonths.interestCoverageAfterCapitalExpendituresCovenantEbitda = Bixby.Utils.calculateMultiplier(
      analyticsLastTwelveMonths.covenantEbitda == null || analyticsLastTwelveMonths.covenantEbitda == undefined
        ? null
        : parseFloat((analyticsLastTwelveMonths.covenantEbitda as any) || '0') - capitalExpenditures,
      analyticsLastTwelveMonths.interestExpense
    );

    // Abl/Revolver
    analyticsLastTwelveMonths.availableLiquidity = (
      parseFloat((analyticsLastTwelveMonths.lineOfCreditAvailability as any) || '0') +
      parseFloat((analyticsLastTwelveMonths.cash as any) || '0')
    ).toFixed(2) as any;

    return { analyticsLastTwelveMonths, analyticsPriorQuarter, analyticsCurrentQuarter };
  }
}
